import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, BarChart3, Shield, Zap, Users, Brain, Sparkles, Heart, CheckCircle2, Eye, Copy, Check as CheckIcon, Wind } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import AvatarFigure from "../components/AvatarFigure";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import { useIsWebView } from "../hooks/useIsWebView";

const Landing = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isWebView = useIsWebView();
  const [emailCopied, setEmailCopied] = useState(false);
  const [heroAvatarState, setHeroAvatarState] = useState("clear");
  const [heroAvatarColor, setHeroAvatarColor] = useState("#002FA7");

  const handleSupportClick = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText("soporte@futura-app.com").then(() => {
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 3000);
    }).catch(() => {
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 3000);
    });
  };

  const features = [
    { icon: Zap, title: t("feat_ai"), description: t("feat_ai_desc"), color: "#002FA7" },
    { icon: BarChart3, title: t("feat_prob"), description: t("feat_prob_desc"), color: "#5B21B6" },
    { icon: Shield, title: t("feat_risk"), description: t("feat_risk_desc"), color: "#EF4444" },
    { icon: Brain, title: t("feat_time"), description: t("feat_time_desc"), color: "#10B981" },
    { icon: Sparkles, title: t("feat_rec"), description: t("feat_rec_desc"), color: "#F59E0B" },
    { icon: Heart, title: t("feat_impact"), description: t("feat_impact_desc"), color: "#EC4899" },
  ];

  const steps = [
    { step: "01", title: t("how_step1_title"), description: t("how_step1_desc") },
    { step: "02", title: t("how_step2_title"), description: t("how_step2_desc") },
    { step: "03", title: t("how_step3_title"), description: t("how_step3_desc") },
  ];

  const trustPillars = [
    { icon: CheckCircle2, title: t("trust1_title"), desc: t("trust1_desc"), color: "#10B981" },
    { icon: Eye, title: t("trust2_title"), desc: t("trust2_desc"), color: "#002FA7" },
    { icon: Users, title: t("trust3_title"), desc: t("trust3_desc"), color: "#5B21B6" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-2 sm:px-6 lg:px-10 xl:px-12 py-2 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 min-w-0">
            <div className="w-6 h-6 sm:w-10 sm:h-10 rounded-lg sm:rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-0.5 sm:p-1.5 flex-shrink-0">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
            <span className="font-['Outfit'] font-semibold text-sm sm:text-xl tracking-tight text-[#111111]">FUTURA</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-[#6B7280] hover:text-[#111111] transition-colors font-medium" data-testid="nav-features">{t("nav_features")}</a>
            <a href="#how-it-works" className="text-[#6B7280] hover:text-[#111111] transition-colors font-medium" data-testid="nav-how-it-works">{t("nav_how_it_works")}</a>
            {!isWebView && <a href="#planes" className="text-[#002FA7] hover:text-[#001d6a] transition-colors font-semibold" data-testid="nav-pricing">{t("nav_pricing")}</a>}
          </div>
          <div className="flex items-center flex-shrink-0" style={{gap: '2px'}}>
            <LanguageSelector variant="light" />
            <Button variant="ghost" onClick={() => navigate("/login")} className="text-[#111111] font-medium px-1 sm:px-4 h-6 sm:h-10 flex-shrink-0" style={{fontSize: 'clamp(9px, 2.5vw, 16px)'}} data-testid="nav-login-btn">
              <span className="hidden sm:inline">{t("nav_login")}</span>
              <span className="sm:hidden">{t("nav_login_short")}</span>
            </Button>
            <Button onClick={() => navigate("/register")} className="bg-[#002FA7] hover:bg-[#001d6a] text-white font-medium rounded-full px-1.5 sm:px-6 h-6 sm:h-10 btn-lift whitespace-nowrap flex-shrink-0" style={{fontSize: 'clamp(9px, 2.5vw, 16px)'}} data-testid="nav-register-btn">
              <span className="hidden sm:inline">{t("nav_register")}</span>
              <span className="sm:hidden">{t("nav_register_short")}</span>
            </Button>
          </div>
        </div>
      </nav>

      {/* === HERO: Decisions FIRST + Avatar companion === */}
      <section className="pt-28 pb-0 relative overflow-hidden bg-[#0a0a0f]" data-testid="avatar-hero-section">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "32px 32px" }} />

        <div className="max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12 relative">

          {/* ROW 1: HEADLINE — Decisions are the core message */}
          <div className="text-center pt-12 lg:pt-20 mb-12 animate-fade-in">
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-[4.25rem] 2xl:text-[5rem] font-semibold text-white leading-[1.1] tracking-tight mb-5 max-w-4xl xl:max-w-5xl 2xl:max-w-6xl mx-auto">
              {t("hero_main_title")}
            </h1>
            <p className="text-base sm:text-lg xl:text-xl text-white/45 max-w-2xl xl:max-w-3xl mx-auto leading-relaxed mb-8">
              {t("hero_main_subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button onClick={() => navigate("/register")} className="bg-gradient-to-r from-[#002FA7] to-[#5B21B6] hover:opacity-90 text-white font-semibold rounded-full px-10 xl:px-12 py-6 xl:py-7 text-lg xl:text-xl btn-lift" data-testid="avatar-hero-cta">
                {t("avatar_landing_cta")} <ArrowRight className="ml-2 w-5 h-5 xl:w-6 xl:h-6" />
              </Button>
              {!isWebView && (
                <a
                  href="#planes"
                  className="group inline-flex items-center gap-2 text-white font-semibold rounded-full px-8 py-5 xl:py-6 text-base xl:text-lg border-2 border-white/30 hover:border-white/60 hover:bg-white/5 transition-all"
                  data-testid="hero-pricing-btn"
                >
                  {t("landing_cta_plans")}
                  <span className="text-amber-400 font-bold ml-1" data-testid="hero-pricing-price-hint">
                    {t("landing_cta_plans_hint")}
                  </span>
                </a>
              )}
            </div>
            {!isWebView && (
              <p className="text-white/40 text-xs sm:text-sm mt-5 flex items-center justify-center gap-2 flex-wrap" data-testid="hero-trustline">
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />{t("landing_trust_free")}</span>
                <span className="text-white/20">·</span>
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />{t("landing_trust_cancel")}</span>
                <span className="text-white/20">·</span>
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />{t("landing_trust_secure")}</span>
              </p>
            )}
          </div>

          {/* ROW 2: Two columns — Decision preview + Avatar */}
          <div className="grid lg:grid-cols-2 gap-8 xl:gap-12 2xl:gap-16 items-start pb-10">

            {/* LEFT: Decision Simulation Preview */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-3xl p-6 sm:p-8 xl:p-10 animate-slide-up flex flex-col">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center flex-shrink-0">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-['Outfit'] font-semibold text-base xl:text-lg">{t("hero_sim_title")}</p>
                  <p className="text-white/60 text-xs sm:text-sm">{t("hero_sim_desc")}</p>
                </div>
              </div>

              {/* Simulated result mockup */}
              <div className="bg-white/[0.04] rounded-2xl p-5 xl:p-6 mb-5 border border-white/[0.05] flex-1 flex flex-col">
                <p className="text-white/55 text-sm italic mb-5">{t("hero_sim_example")}</p>
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-20 h-20 xl:w-24 xl:h-24 rounded-2xl bg-[#10B981]/15 flex items-center justify-center flex-shrink-0">
                    <p className="text-[#10B981] text-3xl xl:text-4xl font-['Outfit'] font-bold leading-none">93%</p>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-['Outfit'] font-semibold text-base xl:text-lg mb-1">{t("result_success")}</p>
                    <p className="text-white/65 text-xs sm:text-sm leading-relaxed">{t("hero_sim_result_desc")}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2.5 mt-auto">
                  <div className="bg-white/[0.05] rounded-xl p-3 xl:p-4 text-center border border-white/[0.04]">
                    <BarChart3 className="w-5 h-5 xl:w-6 xl:h-6 mx-auto mb-1.5 text-[#63b3ed]" />
                    <p className="text-white/75 text-[11px] xl:text-xs font-medium leading-tight">{t("feat_prob")}</p>
                  </div>
                  <div className="bg-white/[0.05] rounded-xl p-3 xl:p-4 text-center border border-white/[0.04]">
                    <Zap className="w-5 h-5 xl:w-6 xl:h-6 mx-auto mb-1.5 text-[#FBBF24]" />
                    <p className="text-white/75 text-[11px] xl:text-xs font-medium leading-tight">{t("hero_sim_risks")}</p>
                  </div>
                  <div className="bg-white/[0.05] rounded-xl p-3 xl:p-4 text-center border border-white/[0.04]">
                    <CheckCircle2 className="w-5 h-5 xl:w-6 xl:h-6 mx-auto mb-1.5 text-[#10B981]" />
                    <p className="text-white/75 text-[11px] xl:text-xs font-medium leading-tight">{t("hero_sim_alternatives")}</p>
                  </div>
                </div>
              </div>

              <p className="text-white/70 text-xs sm:text-sm text-center leading-relaxed px-2">{t("hero_sim_footer")}</p>
            </div>

            {/* RIGHT: Avatar — Your companion */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-3xl p-6 sm:p-8 xl:p-10 animate-slide-up stagger-2 flex flex-col">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#EC4899] to-[#8B5CF6] flex items-center justify-center flex-shrink-0">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-['Outfit'] font-semibold text-base xl:text-lg">{t("hero_avatar_title")}</p>
                  <p className="text-white/60 text-xs sm:text-sm">{t("hero_avatar_desc")}</p>
                </div>
              </div>

              <div className="bg-white/[0.04] rounded-2xl p-5 xl:p-6 mb-5 border border-white/[0.05] flex-1 flex flex-col">
                <div className="flex justify-center mb-5 flex-1 items-center">
                  <div className="relative">
                    <AvatarFigure state={heroAvatarState} color={heroAvatarColor} size={200} interactive={true} />
                    <div className="absolute inset-0 -z-10 rounded-full blur-3xl opacity-25" style={{ backgroundColor: heroAvatarColor }} />
                  </div>
                </div>

                {/* States + Colors */}
                <div className="flex gap-2 mb-3 flex-wrap justify-center">
                  {["dormant", "awakening", "clear", "radiant"].map((s) => (
                    <button key={s} onClick={() => setHeroAvatarState(s)}
                      className={`text-[11px] xl:text-xs font-medium px-3 py-1.5 rounded-full transition-all border ${heroAvatarState === s ? "border-white/40 bg-white/15 text-white" : "border-white/10 text-white/50 hover:text-white/80 hover:border-white/20"}`}
                      data-testid={`avatar-demo-state-${s}`}
                    >
                      {t(`avatar_state_${s}`)}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 justify-center mt-auto" data-testid="avatar-color-palette">
                  {["#002FA7", "#5B21B6", "#10B981", "#D97706", "#E11D48", "#0891B2"].map((c) => (
                    <button key={c} onClick={() => setHeroAvatarColor(c)}
                      className={`w-7 h-7 rounded-full transition-all ${heroAvatarColor === c ? "ring-2 ring-offset-2 ring-offset-[#0a0a0f] ring-white scale-110" : "opacity-50 hover:opacity-80"}`}
                      style={{ backgroundColor: c }}
                      data-testid={`avatar-demo-color-${c.replace('#','')}`}
                    />
                  ))}
                </div>
              </div>

              <p className="text-white/70 text-xs sm:text-sm text-center leading-relaxed px-2">{t("avatar_landing_subtitle")}</p>
            </div>
          </div>

          {/* ROW 3: How it works — 3 steps, large and clear */}
          <div className="grid md:grid-cols-3 gap-5 xl:gap-8 py-10 xl:py-14 border-t border-white/5">
            <div className="flex items-start gap-4 p-5 xl:p-6 rounded-2xl bg-white/[0.02]">
              <div className="w-10 h-10 xl:w-12 xl:h-12 rounded-full bg-[#002FA7]/20 flex items-center justify-center text-[#63b3ed] font-['Outfit'] font-bold text-lg xl:text-xl flex-shrink-0">1</div>
              <div>
                <p className="text-white font-semibold text-sm xl:text-base mb-1">{t("hero_step1_title")}</p>
                <p className="text-white/65 text-sm xl:text-base leading-relaxed">{t("hero_step1_desc")}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 xl:p-6 rounded-2xl bg-white/[0.02]">
              <div className="w-10 h-10 xl:w-12 xl:h-12 rounded-full bg-[#10B981]/20 flex items-center justify-center text-[#10B981] font-['Outfit'] font-bold text-lg xl:text-xl flex-shrink-0">2</div>
              <div>
                <p className="text-white font-semibold text-sm xl:text-base mb-1">{t("hero_step2_title")}</p>
                <p className="text-white/65 text-sm xl:text-base leading-relaxed">{t("hero_step2_desc")}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 xl:p-6 rounded-2xl bg-white/[0.02]">
              <div className="w-10 h-10 xl:w-12 xl:h-12 rounded-full bg-[#EC4899]/20 flex items-center justify-center text-[#EC4899] font-['Outfit'] font-bold text-lg xl:text-xl flex-shrink-0">3</div>
              <div>
                <p className="text-white font-semibold text-sm xl:text-base mb-1">{t("hero_step3_title")}</p>
                <p className="text-white/65 text-sm xl:text-base leading-relaxed">{t("hero_step3_desc")}</p>
              </div>
            </div>
          </div>

          {/* Social proof + ethical */}
          <div className="flex flex-col sm:flex-row items-center justify-between pb-10 gap-6">
            <div className="flex items-center gap-5">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-9 h-9 rounded-full bg-gradient-to-br from-[#002FA7] to-[#5B21B6] border-2 border-[#0a0a0f] flex items-center justify-center text-white text-[10px] font-medium">
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
              <div>
                <p className="text-white/85 text-sm sm:text-base font-semibold">{t("landing_social_proof")}</p>
                <p className="text-white/55 text-xs sm:text-sm">{t("landing_social_sub")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-white/60 flex-shrink-0" />
              <p className="text-white/60 text-xs sm:text-sm">{t("avatar_ethical_banner")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-6 bg-[#F5F5F7]">
        <div className="max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-[#111111] tracking-tight mb-4">{t("features_title")}</h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">{t("features_subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-3xl p-8 card-hover" data-testid={`feature-card`}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: `${f.color}15` }}>
                  <f.icon className="w-7 h-7" style={{ color: f.color }} />
                </div>
                <h3 className="font-['Outfit'] text-xl font-semibold text-[#111111] mb-3">{f.title}</h3>
                <p className="text-[#6B7280] leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-24 px-6 bg-white">
        <div className="max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-[#111111] tracking-tight mb-4">{t("how_title")}</h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">{t("how_subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((item) => (
              <div key={item.step} className="relative" data-testid={`step-${item.step}`}>
                <div className="text-8xl font-['Outfit'] font-black text-[#F5F5F7] absolute -top-4 -left-2">{item.step}</div>
                <div className="relative pt-16 pl-4">
                  <h3 className="font-['Outfit'] text-2xl font-semibold text-[#111111] mb-3">{item.title}</h3>
                  <p className="text-[#6B7280] leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Decide mejor por dentro y por fuera */}
      <section className="py-24 px-6 bg-[#F5F5F7]">
        <div className="max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-[#111111] tracking-tight mb-4">{t("dm_title")}</h2>
            <p className="text-lg text-[#6B7280] max-w-3xl mx-auto">{t("dm_subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Card 1: Decisiones cotidianas */}
            <div className="bg-white rounded-3xl p-8 border border-[#E5E5EA] hover:shadow-lg transition-all" data-testid="dm-card-decisions">
              <div className="w-14 h-14 rounded-2xl bg-[#002FA7]/10 flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-[#002FA7]" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-semibold text-[#111111] mb-3">{t("dm_card1_title")}</h3>
              <p className="text-[#6B7280] leading-relaxed mb-6">{t("dm_card1_desc")}</p>
              <ul className="space-y-3">
                {["dm_card1_b1", "dm_card1_b2", "dm_card1_b3", "dm_card1_b4", "dm_card1_b5"].map((k) => (
                  <li key={k} className="flex items-center gap-3 text-sm text-[#333]">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#002FA7] flex-shrink-0" />
                    {t(k)}
                  </li>
                ))}
              </ul>
            </div>
            {/* Card 2: Claridad emocional */}
            <div className="bg-white rounded-3xl p-8 border border-[#E5E5EA] hover:shadow-lg transition-all" data-testid="dm-card-emotional">
              <div className="w-14 h-14 rounded-2xl bg-[#10B981]/10 flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-[#10B981]" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-semibold text-[#111111] mb-3">{t("dm_card2_title")}</h3>
              <p className="text-[#6B7280] leading-relaxed mb-6">{t("dm_card2_desc")}</p>
              <ul className="space-y-3">
                {["dm_card2_b1", "dm_card2_b2", "dm_card2_b3", "dm_card2_b4", "dm_card2_b5"].map((k) => (
                  <li key={k} className="flex items-center gap-3 text-sm text-[#333]">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#10B981] flex-shrink-0" />
                    {t(k)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Módulos section */}
      <section className="py-24 px-6 bg-white" data-testid="modules-section">
        <div className="max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12">
          <div className="text-center mb-4">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#002FA7] bg-[#002FA7]/5 px-4 py-1.5 rounded-full mb-6">{t("mod_badge")}</span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-[#111111] tracking-tight mb-4">{t("mod_title")}</h2>
            <p className="text-lg text-[#6B7280] max-w-3xl mx-auto">{t("mod_subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mt-12">
            {/* Module 1 - Herramientas interactivas */}
            <div className="relative bg-gradient-to-br from-amber-500/5 to-amber-500/[0.01] rounded-3xl p-8 border border-amber-500/15 hover:border-amber-500/30 transition-all" data-testid="module1-card">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <Wind className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-amber-500 uppercase tracking-wide">{t("mod1_tag")}</span>
                  <h3 className="font-['Outfit'] text-lg font-semibold text-[#111111]">{t("mod1_name")}</h3>
                </div>
              </div>
              <p className="text-[#6B7280] leading-relaxed mb-5">{t("mod1_desc")}</p>
              <div className="grid grid-cols-2 gap-3">
                {["mod1_f1", "mod1_f2", "mod1_f3", "mod1_f4"].map((k) => (
                  <div key={k} className="flex items-center gap-2 text-sm text-[#333]">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                    {t(k)}
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-amber-500/10">
                <p className="text-xs text-[#6B7280]">{t("mod1_plans")}</p>
              </div>
            </div>
            {/* Module 2 - Seguimiento Emocional */}
            <div className="relative bg-gradient-to-br from-[#10B981]/5 to-[#10B981]/[0.01] rounded-3xl p-8 border border-[#10B981]/15 hover:border-[#10B981]/30 transition-all" data-testid="module2-card">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#10B981] uppercase tracking-wide">{t("mod2_tag")}</span>
                  <h3 className="font-['Outfit'] text-lg font-semibold text-[#111111]">{t("mod2_name")}</h3>
                </div>
              </div>
              <p className="text-[#6B7280] leading-relaxed mb-5">{t("mod2_desc")}</p>
              <div className="grid grid-cols-2 gap-3">
                {["mod2_f2", "mod2_f3", "mod2_f4"].map((k) => (
                  <div key={k} className="flex items-center gap-2 text-sm text-[#333]">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#10B981] flex-shrink-0" />
                    {t(k)}
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-[#10B981]/10">
                <p className="text-xs text-[#6B7280]">{t("mod2_plans")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-[#111111] tracking-tight mb-4">{t("trust_title")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {trustPillars.map((p) => (
              <div key={p.title} className="text-center" data-testid="trust-pillar">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: `${p.color}12` }}>
                  <p.icon className="w-7 h-7" style={{ color: p.color }} />
                </div>
                <h3 className="font-['Outfit'] text-lg font-semibold text-[#111111] mb-3">{p.title}</h3>
                <p className="text-[#6B7280] leading-relaxed text-sm">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diario IA Section */}
      <section className="py-24 px-6 bg-[#0a0a0f] text-white" data-testid="diary-ai-landing">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 text-amber-400 text-sm font-semibold uppercase tracking-wide mb-6">
              <Sparkles className="w-4 h-4" />
              {t("landing_diary_badge")}
            </div>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight mb-4">{t("landing_diary_title")}</h2>
            <p className="text-lg text-white/50 max-w-2xl mx-auto">{t("landing_diary_subtitle")}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto mb-10">
            {[t("landing_diary_f1"), t("landing_diary_f2"), t("landing_diary_f3"), t("landing_diary_f4")].map((f, i) => (
              <div key={`diary-feature-${i}`} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-2xl p-5">
                <Sparkles className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-white/70">{f}</span>
              </div>
            ))}
          </div>
          {!isWebView && (
            <div className="text-center">
              <a href="#planes" className="inline-flex items-center bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-black font-semibold rounded-full px-10 py-6 text-lg btn-lift" data-testid="diary-landing-cta">
                {t("landing_diary_cta")} <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          )}
          {isWebView && (
            <p className="text-center text-white/40 text-sm mt-4" data-testid="webview-sub-text-diary">{t("webview_sub_managed")}</p>
          )}
        </div>
      </section>

      {/* Avatar Section removed - now in hero */}

      {/* === PLANS === */}
      {!isWebView && (
      <section id="planes" className="py-24 px-6 bg-[#0a0a0f]" data-testid="landing-plans-section">
        <div className="max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12">
          <div className="text-center mb-14">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-semibold text-white tracking-tight mb-4">{t("pricing_title")}</h2>
            <p className="text-base sm:text-lg xl:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed">{t("pricing_subtitle")}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {/* Descubre */}
            <div className="relative bg-white/[0.04] border border-white/10 rounded-3xl p-8 flex flex-col" data-testid="landing-plan-descubre">
              <p className="text-white/60 text-sm font-medium mb-2">{t("plan_descubre")}</p>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-['Outfit'] text-5xl font-bold text-white">0€</span>
                <span className="text-white/50 text-sm">{t("plan_per_month")}</span>
              </div>
              <p className="text-white/70 text-sm mb-6 leading-relaxed min-h-[3rem]">{t("plan_descubre_desc")}</p>
              <ul className="space-y-2.5 mb-8 text-sm text-white/75 flex-1">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>{t("plan_descubre_feat1")}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>{t("plan_descubre_feat2")}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>{t("plan_descubre_feat3")}</span></li>
              </ul>
              <Button onClick={() => navigate("/register")} variant="ghost" className="w-full bg-white/10 hover:bg-white/15 text-white rounded-full py-5 font-medium" data-testid="landing-plan-descubre-btn">
                {t("plan_start_free")}
              </Button>
            </div>

            {/* Decide - Popular */}
            <div className="relative bg-gradient-to-br from-[#002FA7]/20 to-[#5B21B6]/20 border-2 border-[#002FA7]/50 rounded-3xl p-8 flex flex-col transform md:-translate-y-3 shadow-2xl shadow-[#002FA7]/20" data-testid="landing-plan-decide">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#002FA7] to-[#5B21B6] text-white text-[11px] font-bold px-4 py-1 rounded-full uppercase tracking-wider">{t("pricing_popular")}</span>
              <p className="text-[#63b3ed] text-sm font-medium mb-2">{t("plan_decide")}</p>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-['Outfit'] text-5xl font-bold text-white">5,99€</span>
                <span className="text-white/60 text-sm">{t("plan_per_month")}</span>
              </div>
              <p className="text-white/80 text-sm mb-6 leading-relaxed min-h-[3rem]">{t("plan_decide_desc")}</p>
              <ul className="space-y-2.5 mb-8 text-sm text-white/85 flex-1">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#63b3ed] mt-0.5 flex-shrink-0" /><span>{t("plan_decide_feat1")}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#63b3ed] mt-0.5 flex-shrink-0" /><span>{t("plan_decide_feat2")}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#63b3ed] mt-0.5 flex-shrink-0" /><span>{t("plan_decide_feat3")}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#63b3ed] mt-0.5 flex-shrink-0" /><span>{t("plan_decide_feat4")}</span></li>
              </ul>
              <Button onClick={() => navigate("/register?plan=decide")} className="w-full bg-gradient-to-r from-[#002FA7] to-[#5B21B6] hover:opacity-90 text-white rounded-full py-5 font-semibold" data-testid="landing-plan-decide-btn">
                {t("plan_choose_decide")} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            {/* Avanza */}
            <div className="relative bg-white/[0.04] border border-white/10 rounded-3xl p-8 flex flex-col" data-testid="landing-plan-avanza">
              <p className="text-[#EC4899] text-sm font-medium mb-2">{t("plan_avanza")}</p>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-['Outfit'] text-5xl font-bold text-white">8,99€</span>
                <span className="text-white/50 text-sm">{t("plan_per_month")}</span>
              </div>
              <p className="text-white/70 text-sm mb-6 leading-relaxed min-h-[3rem]">{t("plan_avanza_desc")}</p>
              <ul className="space-y-2.5 mb-8 text-sm text-white/75 flex-1">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#EC4899] mt-0.5 flex-shrink-0" /><span>{t("plan_avanza_feat1")}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#EC4899] mt-0.5 flex-shrink-0" /><span>{t("plan_avanza_feat2")}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#EC4899] mt-0.5 flex-shrink-0" /><span>{t("plan_avanza_feat3")}</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#EC4899] mt-0.5 flex-shrink-0" /><span>{t("plan_avanza_feat4")}</span></li>
              </ul>
              <Button onClick={() => navigate("/register?plan=avanza")} variant="ghost" className="w-full bg-[#EC4899]/15 hover:bg-[#EC4899]/25 text-[#EC4899] rounded-full py-5 font-semibold" data-testid="landing-plan-avanza-btn">
                {t("plan_choose_avanza")} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>

          <p className="text-center text-white/55 text-sm mt-10">{t("pricing_annual_hint")}</p>
        </div>
      </section>
      )}

      {/* CTA */}
      <section className="py-24 px-6 bg-gradient-to-br from-[#002FA7] to-[#5B21B6]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-6">{t("cta_title")}</h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">{t("cta_subtitle")}</p>
          <Button onClick={() => navigate("/register")} className="bg-white hover:bg-[#F5F5F7] text-[#002FA7] font-semibold rounded-full px-10 py-6 text-lg btn-lift" data-testid="cta-register-btn">
            {t("cta_button")}<ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#111111]">
        <div className="max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1.5">
                <FuturaIcon className="w-full h-full text-white" />
              </div>
              <span className="font-['Outfit'] font-semibold text-xl text-white">FUTURA</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <Link to="/terminos" className="text-[#6B7280] hover:text-white transition-colors" data-testid="footer-terminos">{t("footer_terms")}</Link>
              <Link to="/privacidad" className="text-[#6B7280] hover:text-white transition-colors" data-testid="footer-privacidad">{t("footer_privacy")}</Link>
              <Link to="/cookies" className="text-[#6B7280] hover:text-white transition-colors" data-testid="footer-cookies">{t("legal_cookies")}</Link>
              <Link to="/eliminar-cuenta" className="text-[#6B7280] hover:text-white transition-colors" data-testid="footer-eliminar">{t("legal_delete_account")}</Link>
              <button onClick={handleSupportClick} className="text-[#6B7280] hover:text-white transition-colors flex items-center gap-1.5" data-testid="footer-soporte">
                {emailCopied ? <><CheckIcon className="w-3.5 h-3.5 text-emerald-400" /><span className="text-emerald-400">soporte@futura-app.com</span></> : <>{t("footer_support")}: soporte@futura-app.com</>}
              </button>
            </div>
            <p className="text-[#6B7280] text-sm">&copy; {t("footer_rights")}</p>
          </div>
          <div className="border-t border-white/10 pt-6">
            <p className="text-[#6B7280]/60 text-xs text-center leading-relaxed max-w-4xl mx-auto">
              {t("footer_disclaimer")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
